import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { api, ApiError } from "./api";
import { AuthProvider, useAuth } from "./auth";
import { engineLabel } from "./config";
import { navigate, useRoute, type TabKey } from "./route";
import type { AdminIdentity } from "./types";
import { Admins } from "./views/Admins";
import { Allowlist } from "./views/Allowlist";
import { DailyReview } from "./views/DailyReview";
import { Analytics } from "./views/Analytics";
import { FirstAskReport } from "./views/FirstAskReport";
import { Latency } from "./views/Latency";
import { LiveSessions } from "./views/LiveSessions";
import { Login } from "./views/Login";
import { Tasks } from "./views/Tasks";
import { Users } from "./views/Users";
import { Waitlist } from "./views/Waitlist";
import { Registrations, Requests } from "./views/Onboarding";
import "./styles.css";

// Keys must match TAB_KEYS in route.ts — they are the URL path segments.
const TABS: { key: TabKey; label: string }[] = [
  { key: "registrations", label: "Registration & Waitlist" },
  { key: "requests", label: "User requests" },
  { key: "analytics", label: "Analytics" },
  { key: "allowlist", label: "Allowlist" },
  { key: "users", label: "Users" },
  { key: "sessions", label: "Live sessions" },
  { key: "tasks", label: "Tasks" },
  { key: "latency", label: "Latency" },
  { key: "reviews", label: "Daily review" },
  { key: "admins", label: "Admins" },
];

function App() {
  const { profile, signOut } = useAuth();
  const [me, setMe] = useState<AdminIdentity | null>(null);
  // The address bar is the source of truth for which view is open, so a link
  // reopens it and back/forward work.
  const route = useRoute();
  const tab = route.tab;
  const [authError, setAuthError] = useState("");

  // A token proves who you are; /admin/me is what says you may be here. Until it
  // answers, render the sign-in view — never the app shell.
  useEffect(() => {
    if (!profile) {
      setMe(null);
      return;
    }
    let cancelled = false;
    setAuthError("");
    api
      .me()
      .then((identity) => !cancelled && setMe(identity))
      .catch((e) => {
        if (cancelled) return;
        setMe(null);
        setAuthError(
          e instanceof ApiError && e.status === 403
            ? `${profile.email} is not an admin. Ask an owner for access.`
            : e instanceof Error
              ? e.message
              : String(e),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [profile]);

  if (!me) return <Login error={authError} />;

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">Bean Admin</div>
        <nav className="nav">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "navlink active" : "navlink"}
              onClick={() => navigate({ tab: t.key })}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="whoami">
          {/* Local and deployed engines read the SAME production Firestore, so
              the data alone gives no clue which one you are driving. */}
          <span className="badge" title={`Engine: ${engineLabel}`}>
            {engineLabel}
          </span>
          <span className="muted">{me.email}</span>
          <span className="badge">{me.role}</span>
          <button className="ghost small" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        {tab === "analytics" && <Analytics />}
        {tab === "report" && <FirstAskReport />}
        {tab === "waitlist" && (
          <Waitlist initialSubmissionId={route.waitlistId} />
        )}
        {tab === "registrations" && <Registrations />}
        {tab === "requests" && <Requests />}
        {tab === "allowlist" && <Allowlist />}
        {tab === "users" && <Users />}
        {tab === "sessions" && <LiveSessions />}
        {tab === "tasks" && <Tasks />}
        {tab === "latency" && <Latency />}
        {tab === "reviews" && <DailyReview />}
        {tab === "admins" && <Admins me={me} />}
      </main>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </StrictMode>,
);
